/**
 * PlanetNode — custom React Flow node for a galaxy-map planet.
 *
 * Renders as a white dot (the draggable core) sitting above a single donut
 * connect ring that only appears on hover. Dragging the dot body MOVES the node;
 * dragging out from the ring starts a connection.
 *
 * The dot carries NO information of its own — no start/final glyph and no hover
 * card. Everything about a planet lives in the PlanetEditModal, which a single
 * click opens (see TrlReactFlowCanvas); double-clicking the dot opens it too.
 * This mirrors the player map, where a hover card competing with the click panel
 * taught players that hovering was all there was.
 */

import { memo } from 'react';
import { Handle, Position, type NodeProps, type Node } from '@xyflow/react';
import { getMapIconUrl } from '@swtorbit/shared-assets';
import type { CSSProperties } from 'react';

export interface PlanetNodeData extends Record<string, unknown> {
  label: string;
  derivedLevel?: number;
  isStart: boolean;
  isFinal: boolean;
  isCheckpoint: boolean;
  thumbUrl?: string;
  /** Custom node-marker id (from the shared-assets registry), or null for the default white dot. */
  icon: string | null;
  /** CSS colour the node's pulsing halo is tinted with, or null for the default white. */
  iconColor: string | null;
  readOnly: boolean;
  /** Open this planet's editor. Bound to double-click here; single click is wired on the canvas. */
  onOpen: () => void;
}

export type PlanetFlowNode = Node<PlanetNodeData, 'planet'>;

function PlanetNodeComponent({ data, selected }: NodeProps<PlanetFlowNode>) {
  // Only what the DOT draws. The rest of the node data (label, round, thumbnail,
  // start/final flags) is the PlanetEditModal's to show.
  const { isCheckpoint, icon, iconColor, readOnly, onOpen } = data;

  // No ring: a hard circle drawn around irregular marker artwork fought the
  // shape-accurate scan ripples, so selection and checkpoint state now read
  // through the ripple/halo COLOUR instead (`--trl-glow-state`, set by these
  // modifiers in _trl-map-builder.scss). State still wins over the admin's
  // per-node glow colour, because that one arrives as `--trl-glow-color-admin`.
  const dotStateClass = selected
    ? 'trl-map-builder__dot--selected'
    : isCheckpoint
      ? 'trl-map-builder__dot--checkpoint'
      : '';

  // Custom marker: the dot's white fill is replaced by the admin's chosen SVG
  // artwork, drawn as-authored in a child (`__dot-art`) so the state ring stays
  // on the dot itself. The admin colour tints the pulsing HALO, not the artwork —
  // the illustration carries its own colours and is never recoloured.
  //
  // Both vars are declared on the dot rather than on the artwork child, because
  // the halo layer needs to read `--trl-icon-src` too (it masks the same SVG to
  // trace the marker's contour). Custom properties inherit, so each child picks
  // up what it needs. See .trl-map-builder__dot-art / __dot-glow.
  const iconUrl = icon ? getMapIconUrl(icon) : '';
  const iconStyle = iconUrl
    ? ({
        '--trl-icon-src': `url("${iconUrl}")`,
        '--trl-glow-color-admin': iconColor ?? undefined,
      } as CSSProperties)
    : undefined;

  return (
    <div className="trl-map-builder__dot-wrap">
      {/* React Flow only renders an edge when BOTH of its endpoint nodes expose a
          handle with resolvable bounds — otherwise getEdgePosition() returns null
          and the edge is silently dropped (EdgeWrapper bails on null source/target
          coords). So a handle must ALWAYS be present, even on a locked map, or every
          connecting line vanishes the instant the game leaves draft. When editing
          it is the interactive connect ring; when locked it collapses to a hidden,
          non-connectable anchor that exists purely so edges keep resolving. In loose
          connection mode this single source handle serves as both endpoints. */}
      <Handle
        type="source"
        position={Position.Top}
        isConnectable={!readOnly}
        className={readOnly ? 'trl-map-builder__handle-anchor' : 'trl-map-builder__handle-ring'}
      />

      <div
        className={`trl-map-builder__dot ${dotStateClass}${iconUrl ? ' trl-map-builder__dot--icon' : ''}`}
        style={iconStyle}
        onDoubleClick={onOpen}
      >
        {/* Shape-accurate resting halo, behind everything else. The blur lives on
            the WRAPPER and the mask on the CHILD because CSS applies filter before
            mask — blurring the masked element would clip the glow away entirely.
            Always rendered: with no marker set it falls back to a round halo. */}
        <span className="trl-map-builder__dot-glow" aria-hidden="true">
          <span className="trl-map-builder__dot-glow-shape" />
        </span>
        {/* Seismic scan ripples — three outlines of the marker's own shape
            expanding outwards and fading, staggered a third of a period apart.
            Mirrors the scanning rings of the seismic tool overlay. */}
        <span className="trl-map-builder__dot-ripples" aria-hidden="true">
          <span className="trl-map-builder__dot-ripple" />
          <span className="trl-map-builder__dot-ripple trl-map-builder__dot-ripple--2" />
          <span className="trl-map-builder__dot-ripple trl-map-builder__dot-ripple--3" />
        </span>
        {iconUrl && <span className="trl-map-builder__dot-art" />}
      </div>
    </div>
  );
}

export const PlanetNode = memo(PlanetNodeComponent);
